/*
 * LUMERA Sticky CTA — shown while the hero purchase area (trigger) has scrolled out of view
 * above the viewport; hidden again when the purchase area is back on screen, so two buy
 * buttons are never visible at once.
 * While hidden it is inert and aria-hidden, so it is out of the tab order and the accessibility tree.
 * Motion is CSS-only and respects prefers-reduced-motion (lumera-base.css).
 */
if (!customElements.get('lumera-sticky-cta')) {
  customElements.define(
    'lumera-sticky-cta',
    class LumeraStickyCta extends HTMLElement {
      connectedCallback() {
        const triggerId = this.dataset.trigger;
        const trigger = triggerId ? document.getElementById(triggerId) : null;
        if (!trigger || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.setVisible(scrolledPast);
          });
        });
        this.observer.observe(trigger);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        this.setBodyOffset(false);
      }

      setVisible(visible) {
        if (visible === this.classList.contains('is-visible')) return;
        this.classList.toggle('is-visible', visible);

        if (visible) {
          this.removeAttribute('inert');
          this.removeAttribute('aria-hidden');
        } else {
          // Move focus out before hiding so keyboard users are never left on an inert control.
          if (this.contains(document.activeElement)) document.activeElement.blur();
          this.setAttribute('inert', '');
          this.setAttribute('aria-hidden', 'true');
        }
        this.setBodyOffset(visible);
      }

      // Reserve space so the fixed bar never covers the end of the page while visible.
      setBodyOffset(visible) {
        if (visible) {
          this.syncOffset();
          if (!this.resizeObserver && 'ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.syncOffset());
            this.resizeObserver.observe(this);
          }
          document.body.classList.add('lumera-has-sticky-cta');
        } else {
          document.body.classList.remove('lumera-has-sticky-cta');
          document.body.style.removeProperty('--lumera-sticky-cta-height');
        }
      }

      syncOffset() {
        document.body.style.setProperty('--lumera-sticky-cta-height', `${this.offsetHeight}px`);
      }
    }
  );
}
